import i18n from 'i18next';

import { downloadFile, openFile, base64ToBuffer } from '../helpers/utils';
import {
  DownloadAssortmentExcelFileParam,
  DownloadOutputFileParam,
  DownloadMyDataFileParam,
  DownloadPEGxFileParam,
  GetFileParams,
  GetProtoPogFileParams,
  DownloadFileTypes
} from '../types/requests';
import msalService from './msalService';
import { getConfig } from '../utils/getConfig';
import { APIMSUBSCRIPTION_DEFAULT_VALUE, AppRoutes } from '../utils/constants';
import { openInNewTab } from '../utils/global.utils';
import { DownloadOutputFilesResponse, PDFResponse } from '../types/responses';
import { FileParams } from '../pages/HomePage/Provider/HomePage.types';
import { API } from './apiClient.constants';

export class FetchClient {
  baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async getAccessToken() {
    try {
      const accessToken = await msalService.checkAccessToken();

      if (accessToken) {
        return accessToken;
      }
    } catch (e) {
      console.log('msalHandleAccessToken error', e);
    }
  }

  getHeaders(token: string = '') {
    const headers = new Headers();
    const APIMSUBSCRIPTION = getConfig('APIMSUBSCRIPTION') || APIMSUBSCRIPTION_DEFAULT_VALUE;

    headers.append('Authorization', token);
    headers.append('Content-Type', 'application/json');
    headers.append('Ocp-Apim-Subscription-Key', APIMSUBSCRIPTION);

    return headers;
  }

  async getRequestData(url: string) {
    const requestURL = `${this.baseUrl}${url}`;
    const token = `Bearer ${await this.getAccessToken()}`;

    return {
      headers: this.getHeaders(token),
      requestURL,
    };
  }

  async requestHandlerPost(url: string, data = {}) {
    try {
      const { requestURL, headers } = await this.getRequestData(url);

      const raw = JSON.stringify(data);
      const requestOption = {
        method: 'POST',
        body: raw,
        redirect: 'follow',
        headers,
      } as const;

      const response = await fetch(requestURL, requestOption);

      if (response.ok) {
        return response;
      }
    } catch (error) {
      console.log(error);
    }
  }

  async requestHandlerGet(url: string) {
    try {
      const { requestURL, headers } = await this.getRequestData(url);

      const requestOption = {
        method: 'GET',
        redirect: 'follow',
        responseType: 'blob',
        headers,
      } as const;

      const response = await fetch(requestURL, requestOption);

      if (response.ok) {
        return response;
      }
    } catch (error) {
      console.log(error);
    }
  }

  async getReleaseNotesPDF(
    { fileName, language, url }: FileParams
  ): Promise<any> {

    const pdfResponse = await this.requestHandlerPost(url, { language, release_file_name: fileName });

    if (pdfResponse?.ok) {
      const pdfBlob = await pdfResponse.blob();

      await openFile(pdfBlob, fileName);
    }
  }

  async downloadAssortmentExcelFile(
    params: DownloadAssortmentExcelFileParam
  ): Promise<void> {
    const { fileName, storeNumber, aisleName, reqBody } = params;
    const excelFileName = `${fileName}.${DownloadFileTypes.XLSM}`;
    const url = 'pog-setup-guide/export-assortment';
    const data = {
      responseType: 'blob',
      reqBody,
      aisleName,
      storeNumber,
      lang: i18n.language
    };

    try {
      const excelResponse = await this.requestHandlerPost(url, data);

      if (excelResponse?.ok) {
        const excelBlob = await excelResponse.blob();

        await downloadFile(excelBlob, excelFileName);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async getWhatsNewPDF(
    { fileName, language, url }: FileParams
  ): Promise<any> {
    const pdfResponse = await this.requestHandlerPost(url, { language, orchestration_file_name: fileName });

    if (pdfResponse?.ok) {
      const pdfBlob = await pdfResponse.blob();

      await openFile(pdfBlob, fileName);
    }
  }

  async downloadOutputFiles(
    params: DownloadOutputFileParam
  ): Promise<DownloadOutputFilesResponse> {
    const { csvUrl, pdfUrl, fileName, alerts, lang, includeImages } = params;
    const csvFilename = `${fileName}.${DownloadFileTypes.CSV}`;
    const pdfFilename = `${fileName}.${DownloadFileTypes.PDF}`;
    let csvDownloaded: boolean | undefined;

    try {
      if (csvUrl !== null) {
        const csvResponse = await this.requestHandlerGet(csvUrl);

        csvDownloaded = Boolean(csvResponse?.ok);

        if (csvResponse?.ok) {
          const csvBlob = await csvResponse.blob();

          await downloadFile(csvBlob, csvFilename);
        }
      }

      if (includeImages) {
        const pdfResponse = await this.requestHandlerGet(pdfUrl);
        const pdfResponseObj: PDFResponse = await pdfResponse?.json();

        const { status, data } = pdfResponseObj;

        if (status === 'DONE' && data !== null) {
          const pdfBlob = new Blob([base64ToBuffer(data)], { type: 'application/pdf' });

          await downloadFile(pdfBlob, pdfFilename);

          return { status: 'DONE', csvDownloaded };
        }

        return { status, csvDownloaded };
      } else {
        const pdfResponse = await this.requestHandlerPost(pdfUrl, {
          responseType: 'blob',
          alerts,
          lang,
          includeImages
        });

        if (pdfResponse?.ok) {
          const pdfBlob = await pdfResponse.blob();

          await downloadFile(pdfBlob, pdfFilename);
        }

        // `status` is only consumed for the images PDF; for this one it feeds telemetry (SeedDownload.success).
        return { status: pdfResponse?.ok ? 'DONE' : 'FAILED', csvDownloaded };
      }
    } catch (error) {
      return { status: 'FAILED', csvDownloaded };
    }
  }

  async pollForImagePdf(
    pdfUrl: string,
    maxAttempts = 20,
    intervalMs = 3000
  ): Promise<PDFResponse> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const pdfResponse = await this.requestHandlerGet(pdfUrl);
      const pdfResponseObj: PDFResponse = await pdfResponse?.json();

      if (pdfResponseObj.status !== 'STARTED') {
        return pdfResponseObj;
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }

    return { status: 'FAILED', data: null };
  }

  async openOutputPdfInNewTab(
    params: Pick<DownloadOutputFileParam, 'pdfUrl' | 'fileName' | 'alerts' | 'lang' | 'includeImages'>
  ): Promise<boolean> {
    const { pdfUrl, fileName, alerts, lang, includeImages } = params;
    const pdfFilename = `${fileName}.${DownloadFileTypes.PDF}`;

    try {
      if (includeImages) {
        // The images PDF is generated asynchronously by the server, so the first request(s)
        // may come back with status 'STARTED' before the file is actually ready.
        const { status, data } = await this.pollForImagePdf(pdfUrl);

        if (status === 'DONE' && data !== null) {
          const pdfBlob = new Blob([base64ToBuffer(data)], { type: 'application/pdf' });

          await openFile(pdfBlob, pdfFilename);

          return true;
        }
      } else {
        const pdfResponse = await this.requestHandlerPost(pdfUrl, {
          responseType: 'blob',
          alerts,
          lang,
          includeImages: false,
        });

        if (pdfResponse?.ok) {
          const pdfBlob = await pdfResponse.blob();

          await openFile(pdfBlob, pdfFilename);

          return true;
        }
      }
    } catch (error) {
      console.error(error);
    }

    return false;
  }

  async downloadPEGxFile(
    params: DownloadPEGxFileParam
  ): Promise<boolean> {
    try {
      const { url, alerts, lang } = params;
      const pdfResponse = await this.requestHandlerPost(url, {
        responseType: 'blob',
        alerts,
        lang,
      });

      if (pdfResponse?.ok) {
        const pdfBlob = await pdfResponse.blob();
        const fileURL = URL.createObjectURL(pdfBlob);

        openInNewTab(AppRoutes.PEGx, { fileURL });

        return true;
      }
    } catch (error) {
      console.error(error);
    }

    return false;
  }

  async downloadMyDataFiles(
    params: DownloadMyDataFileParam
  ): Promise<boolean> {
    const {
      name,
      lang,
      store_num,
      aisle_code,
      product_nums,
      starting_assortment,
      demand,
      labels,
      use_iss,
      use_prom,
      aisle_geometry,
    } = params;
    const filename = `${name}.${DownloadFileTypes.XLSX}`;

    try {
      const response = await this.requestHandlerPost(API.printWithMyData, {
        store_num,
        lang,
        aisle_code,
        product_nums,
        starting_assortment,
        demand,
        labels,
        use_iss,
        use_prom,
        aisle_geometry
      });

      if (response?.ok) {
        const blob = await response.blob();

        await downloadFile(blob, filename);
      }

      return !!response?.ok;
    } catch (error) {
      console.log(error);

      throw Error(error);
    }
  }

  async getFile(
    { pogNumber: PogNumber }: GetFileParams
  ): Promise<any> {
    const pdfUrl = API.getFile;
    const pdfResponse = await this.requestHandlerPost(pdfUrl, { PogNumber });

    if (pdfResponse?.ok) {
      const pdfBlob = await pdfResponse.blob();

      return pdfBlob;
    }

    return null;
  }

  async getProtoPogFile(
    params: GetProtoPogFileParams
  ): Promise<any> {
    const { pogNumber: pog_number, aisleCode: aisle_code, storeNumber: store_num } = params;
    const pdfUrl = API.getProtoPogFile;
    const pdfResponse = await this.requestHandlerPost(pdfUrl, { pog_number, aisle_code, store_num });

    if (pdfResponse?.ok) {
      const pdfBlob = await pdfResponse.blob();

      return pdfBlob;
    } else {
      return await this.getFile(params);
    }
  }

}
